import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Helmet } from "react-helmet-async";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useEffect, useMemo, lazy, Suspense } from "react";
import i18n from "@/i18n/config";
import { getHreflangEntries, getOgLocale, getHtmlLang, ALL_LANGUAGES } from "@/lib/seoUtils";
import { InstallPrompt } from "@/components/pwa/InstallPrompt";
import { UpdateBanner } from "@/components/pwa/UpdateBanner";
import { AppLayout } from "@/components/layout/AppLayout";
import { AuthProvider } from "@/contexts/AuthContext";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import Index from "./pages/Index";
// Initialize cron jobs on app load
import "@/lib/activateCronJobs";

// Lazy-loaded pages (code-split for faster initial load)
const Dashboard = lazy(() => import("./pages/Dashboard"));
const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const Admin = lazy(() => import("./pages/Admin"));
const Analysis = lazy(() => import("./pages/Analysis"));
const AdminAnalysesDashboard = lazy(() => import("./pages/AdminAnalysesDashboard"));
const AdminCopilotMetrics = lazy(() => import("./pages/AdminCopilotMetrics"));
const AdminAIUsage = lazy(() => import("./pages/AdminAIUsage"));
const AdminSEOInsights = lazy(() => import("./pages/AdminSEOInsights"));
const AdminPicksPerformance = lazy(() => import("./pages/AdminPicksPerformance"));
const AdminSG7ReportsPicks = lazy(() => import("./pages/AdminSG7ReportsPicks"));
const AdminStrategyLab = lazy(() => import("./pages/AdminStrategyLab"));
const AdminBacktesting = lazy(() => import("./pages/AdminBacktesting"));
const AdminPitch = lazy(() => import("./pages/AdminPitch"));
const AdminUsers = lazy(() => import("./pages/AdminUsers"));
const AdminTestSportmonks = lazy(() => import("./pages/AdminTestSportmonks"));
const AdminSocialPosts = lazy(() => import("./pages/AdminSocialPosts"));
const AdminInsightsDashboard = lazy(() => import("./pages/AdminInsightsDashboard"));
const PalpiteStudioHome = lazy(() => import("./pages/PalpiteStudioHome"));
const PalpiteStudioWizard = lazy(() => import("./pages/PalpiteStudioWizard"));
const PalpiteStudioJob = lazy(() => import("./pages/PalpiteStudioJob"));
const AdminPalpiteCharacters = lazy(() => import("./pages/AdminPalpiteCharacters"));
const AdminSportmonksUsage = lazy(() => import("./pages/AdminSportmonksUsage"));
const Auth = lazy(() => import("./pages/Auth"));
const Profile = lazy(() => import("./pages/Profile"));
const Settings = lazy(() => import("./pages/Settings"));
const NotFound = lazy(() => import("./pages/NotFound"));
const GameDetails = lazy(() => import("./pages/GameDetails"));
const SG7Picks = lazy(() => import("./pages/SG7Picks"));
const RadarDemo = lazy(() => import("./pages/RadarDemo"));
const WorldCup2026 = lazy(() => import("./pages/WorldCup2026"));
const JournalHome = lazy(() => import("./pages/journal").then(m => ({ default: m.JournalHome })));
const JournalMatch = lazy(() => import("./pages/journal").then(m => ({ default: m.JournalMatch })));
const JournalShare = lazy(() => import("./pages/journal").then(m => ({ default: m.JournalShare })));

const queryClient = new QueryClient();

// Lightweight inline fallback for route transitions
const PageFallback = () => (
  <div className="min-h-screen bg-background flex items-center justify-center">
    <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

// Inner component that uses QueryClient hooks
const AppContent = () => {
  // RTL support for Arabic + dynamic <html lang> for SEO + manual scroll restoration
  useEffect(() => {
    // Disable browser auto scroll restoration so useScrollRestore controls it
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
    const updateDir = (lng: string) => {
      document.documentElement.dir = lng === 'ar' ? 'rtl' : 'ltr';
      document.documentElement.lang = getHtmlLang(lng);
    };
    updateDir(i18n.language);
    i18n.on('languageChanged', updateDir);
    return () => { i18n.off('languageChanged', updateDir); };
  }, []);

  // Hreflang entries for global SEO (same URL, multiple langs)
  const hreflangEntries = useMemo(() => getHreflangEntries('/'), []);

  // Listener para mensagens do Service Worker (iOS PWA workaround)
  useEffect(() => {
    const handleSWMessage = (event: MessageEvent) => {
      if (event.data?.type === 'NAVIGATE_TO') {
        console.log('📱 [App] Received NAVIGATE_TO from SW:', event.data.url);
        const url = new URL(event.data.url);
        window.location.href = url.pathname + url.search;
      }
    };

    navigator.serviceWorker?.addEventListener('message', handleSWMessage);
    
    return () => {
      navigator.serviceWorker?.removeEventListener('message', handleSWMessage);
    };
  }, []);

  const globalJsonLd = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "StudioGool7",
    "alternateName": ["SG7", "Studio Gool 7", "SG7 Laboratory"],
    "url": "https://studiogool7.com",
    "description": "Predictive football analytics studio delivering advanced match analysis, smart predictions, and daily betting picks for the world's top leagues.",
    "inLanguage": ALL_LANGUAGES,
    "publisher": {
      "@type": "Organization",
      "name": "StudioGool7",
      "url": "https://studiogool7.com",
      "logo": {
        "@type": "ImageObject",
        "url": "https://studiogool7.com/favicon.png"
      },
      "sameAs": []
    },
    "hasPart": [
      { "@type": "SiteNavigationElement", "name": "Home", "url": "https://studiogool7.com/" },
      { "@type": "SiteNavigationElement", "name": "SG7 Journal", "url": "https://studiogool7.com/journal" },
      { "@type": "SiteNavigationElement", "name": "SG7 Picks", "url": "https://studiogool7.com/sg7-picks" },
      { "@type": "SiteNavigationElement", "name": "Labs", "url": "https://studiogool7.com/labs" },
      { "@type": "SiteNavigationElement", "name": "About", "url": "https://studiogool7.com/sobre" },
      { "@type": "SiteNavigationElement", "name": "Contact", "url": "https://studiogool7.com/contato" }
    ],
    "about": {
      "@type": "Thing",
      "name": "Football Analytics",
      "description": "Advanced statistical analysis, match predictions, betting picks, league standings, and team performance data for Premier League, La Liga, Serie A, Bundesliga, Ligue 1, Champions League, and more."
    }
  };

  return (
    <TooltipProvider>
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(globalJsonLd)}</script>
        <meta property="og:locale" content={getOgLocale(i18n.language)} />
        {hreflangEntries.map(({ hreflang, href }) => (
          <link key={hreflang} rel="alternate" hrefLang={hreflang} href={href} />
        ))}
      </Helmet>
      <UpdateBanner />
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AuthProvider>
          <Suspense fallback={<PageFallback />}>
            <Routes>
              {/* Journal routes — standalone portal, no sidebar/bottom nav */}
              <Route path="/journal" element={<JournalHome />} />
              <Route path="/journal/share/:fixtureId" element={<JournalShare />} />
              <Route path="/journal/match/:fixtureId" element={<JournalMatch />} />

              {/* All SG7 app routes wrapped in AppLayout (sidebar + bottom nav) */}
              <Route element={<AppLayout />}>
                <Route path="/" element={<Index />} />
                <Route path="/labs" element={<Dashboard />} />
                <Route path="/sobre" element={<About />} />
                <Route path="/contato" element={<Contact />} />
                <Route path="/auth" element={<Auth />} />
                <Route path="/definicoes" element={<Settings />} />
                <Route path="/game/:id" element={<GameDetails />} />
                <Route path="/jogo/:id" element={<GameDetails />} />
                <Route path="/analysis/:fixtureId" element={<Analysis />} />
                <Route path="/sg7-picks" element={<SG7Picks />} />
                <Route path="/world-cup" element={<WorldCup2026 />} />
                <Route path="/mundial" element={<Navigate to="/world-cup" replace />} />
                <Route
                  path="/perfil" 
                  element={
                    <ProtectedRoute>
                      <Profile />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/admin" 
                  element={
                    <ProtectedRoute requireAdmin={true} allowModerator={true}>
                      <Admin />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/admin/analyses-dashboard" 
                  element={
                    <ProtectedRoute requireAdmin={true}>
                      <AdminAnalysesDashboard />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/admin/copilot-metrics" 
                  element={
                    <ProtectedRoute requireAdmin={true}>
                      <AdminCopilotMetrics />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/admin/ai-usage" 
                  element={
                    <ProtectedRoute requireAdmin={true}>
                      <AdminAIUsage />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/admin/seo-insights" 
                  element={
                    <ProtectedRoute requireAdmin={true}>
                      <AdminSEOInsights />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/admin/picks-performance" 
                  element={
                    <ProtectedRoute requireAdmin={true} allowModerator={true}>
                      <AdminPicksPerformance />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/admin/sg7-relatorios-picks" 
                  element={
                    <ProtectedRoute requireAdmin={true} allowModerator={true}>
                      <AdminSG7ReportsPicks />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/admin/strategy-lab" 
                  element={
                    <ProtectedRoute requireAdmin={true} allowModerator={true}>
                      <AdminStrategyLab />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/admin/backtesting" 
                  element={
                    <ProtectedRoute requireAdmin={true} allowModerator={true}>
                      <AdminBacktesting />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/admin/pitch" 
                  element={
                    <ProtectedRoute requireAdmin={true} allowModerator={true}>
                      <AdminPitch />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/admin/users" 
                  element={
                    <ProtectedRoute requireAdmin={true}>
                      <AdminUsers />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/admin/test-sportmonks" 
                  element={
                    <ProtectedRoute requireAdmin={true}>
                      <AdminTestSportmonks />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/admin/social-posts" 
                  element={
                    <ProtectedRoute requireAdmin={true}>
                      <AdminSocialPosts />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/admin/sportmonks-usage" 
                  element={
                    <ProtectedRoute requireAdmin={true}>
                      <AdminSportmonksUsage />
                    </ProtectedRoute>
                  } 
                />
                <Route path="/admin/palpite-studio" element={
                  <ProtectedRoute requireAdmin={true}><PalpiteStudioHome /></ProtectedRoute>
                } />
                <Route path="/admin/palpite-studio/novo" element={
                  <ProtectedRoute requireAdmin={true}><PalpiteStudioWizard /></ProtectedRoute>
                } />
                <Route path="/admin/palpite-studio/job/:id" element={
                  <ProtectedRoute requireAdmin={true}><PalpiteStudioJob /></ProtectedRoute>
                } />
                <Route path="/admin/palpite-studio/personagens" element={
                  <ProtectedRoute requireAdmin={true}><AdminPalpiteCharacters /></ProtectedRoute>
                } />
                <Route 
                  path="/admin/insights-dashboard" 
                  element={
                    <ProtectedRoute requireAdmin={true}>
                      <AdminInsightsDashboard />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/radar-demo" 
                  element={
                    <ProtectedRoute requireAdmin={true}>
                      <RadarDemo />
                    </ProtectedRoute>
                  } 
                />
                {/* Redirect antigo /dashboard para /labs */}
                <Route path="/dashboard" element={<Navigate to="/labs" replace />} />
                {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                <Route path="*" element={<NotFound />} />
              </Route>
            </Routes>
          </Suspense>
        </AuthProvider>
      </BrowserRouter>
      <InstallPrompt />
    </TooltipProvider>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AppContent />
  </QueryClientProvider>
);

export default App;
